import { ApiClientWrapper } from '@spike/api-client';
import { NewAppointment } from '@spike/new-booking-model';
import { getRoomUnitId } from 'components/CreateBooking/CreateBooking';
import {
    resolveDiscountPayloadForSave,
    resolveOrderItemPriceForSave,
    shouldApplyMembershipRedeemOnSave
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { Daycare, Feeding, Medication } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { getDaycareAppointmentTasksToSave } from 'utils/daycareAppointmentTasks';
import { buildDaycareVariableDataPerSlot } from 'utils/daycareBulkSlotBuilder';
import { normalizeDaycareForSlotSave } from 'utils/daycareDateSlots';
import { FeedingSlotTimes } from 'utils/feedingSlotTimes';

export interface BuildDaycareAppointmentsParams {
    apiClientWrapper: ApiClientWrapper;
    bookingId: number;
    daycare: Daycare;
    feedings: Array<Feeding>;
    medications: Array<Medication>;
    petId: number;
    customerId: number;
    createdByStaffId: number;
    fallbackDate: Moment;
    timezone: string;
    feedingSlots: FeedingSlotTimes;
    status: number;
    marketplaceAddressId: number;
}

export const buildDaycareAppointments = ({
    apiClientWrapper,
    bookingId,
    daycare,
    feedings,
    medications,
    petId,
    customerId,
    createdByStaffId,
    fallbackDate,
    timezone,
    feedingSlots,
    status,
    marketplaceAddressId
}: BuildDaycareAppointmentsParams): Array<NewAppointment> => {
    const normalizedDaycare = normalizeDaycareForSlotSave(daycare, timezone);
    const fallbackInTz = fallbackDate.clone().tz(timezone);
    const variableDataPerSlot = buildDaycareVariableDataPerSlot(
        normalizedDaycare,
        feedings,
        medications,
        fallbackInTz,
        timezone,
        false
    );

    return variableDataPerSlot.map(variableData => {
        const roomUnitId =
            variableData.service?.roomUnitId ??
            getRoomUnitId(variableData.service?.room, 'daycare', variableData.service?.roomUnitId);
        const tasks = getDaycareAppointmentTasksToSave(variableData, roomUnitId, timezone, feedingSlots);
        const membershipDiscount = resolveDiscountPayloadForSave(variableData.itemData?.discount, variableData.service);
        const enrollmentId = shouldApplyMembershipRedeemOnSave(variableData.service)
            ? variableData.service?.enrollment?.id
            : undefined;
        const dateToSave = variableData.date!.clone().tz(timezone).seconds(0).milliseconds(0);
        const dateEndToSave = variableData.dateEnd
            ? variableData.dateEnd.clone().tz(timezone).seconds(0).milliseconds(0)
            : dateToSave.clone().add(variableData.service?.durationInMinutes || 0, 'minutes');

        return {
            booking_id: bookingId,
            uuid: apiClientWrapper.uuid(),
            address_id: marketplaceAddressId,
            booked_at: dateToSave,
            booking_end_at: dateEndToSave,
            customer_id: customerId,
            pet_id: petId,
            service_id: variableData.service?.service.id!,
            staff_id: undefined,
            created_by: createdByStaffId,
            notes: '',
            order_item_price: resolveOrderItemPriceForSave(variableData.service),
            room_unit_id: roomUnitId,
            status,
            tasks,
            discount_amount: membershipDiscount?.amount,
            discount_percentage: membershipDiscount?.percentage,
            ...(enrollmentId != null
                ? {
                      redeemType: 'Enrollment' as const,
                      redeemId: enrollmentId
                  }
                : {})
        };
    });
};
